import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Monitor,
  Terminal,
  Plus,
  Trash2,
  Check,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Loader2,
  X,
  Server,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import type {
  Sandbox,
  CheckoutRequest,
  SandboxShape,
  PortConfiguration,
  PortAuthPolicy,
  PortProtocol,
  ImageCatalogEntry,
} from '@/types';
import { SHAPE_PRESETS } from '@/types';
import { usePoolStore } from '@/stores/usePoolStore';
import { useImageCatalogStore } from '@/stores/useDiskImageStore';
import { useSandboxStore } from '@/stores/useSandboxStore';
import { formatShape, formatDuration } from '@/utils/format';
import { ApiError } from '@/services/api';
import { ApiErrorModal } from './ApiErrorModal';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BASE_STEPS = ['Image', 'Configuration', 'Policies & Ports', 'Review'] as const;
const POOL_STEPS = ['Pool', 'Image', 'Configuration', 'Policies & Ports', 'Review'] as const;

const PORT_AUTH_POLICIES: PortAuthPolicy[] = ['Owner', 'Custom'];
const PORT_PROTOCOLS: PortProtocol[] = ['Http', 'Http2'];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CheckoutWizardProps {
  onClose: () => void;
  onCreated?: (sandbox: Sandbox) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CheckoutWizard({ onClose, onCreated }: CheckoutWizardProps) {
  // --- Stores ---
  const { pools, selectedPoolIds, getPoolById } = usePoolStore();
  const { images, fetchImages, getImagesByPool } = useImageCatalogStore();
  const { checkoutSandbox } = useSandboxStore();

  // Multi-pool: show pool selection step first
  const isMultiPool = selectedPoolIds.length !== 1;
  const STEPS = isMultiPool ? POOL_STEPS : BASE_STEPS;
  const maxStep = STEPS.length - 1;

  // --- Wizard state ---
  const [step, setStep] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<ApiError | null>(null);

  // Pool selection (only used in multi-pool mode)
  const [wizardPoolId, setWizardPoolId] = useState<string>(selectedPoolIds[0] ?? '');

  // The effective pool for image filtering etc.
  const selectedPoolId = isMultiPool ? wizardPoolId : (selectedPoolIds[0] ?? null);
  const pool = selectedPoolId ? getPoolById(selectedPoolId) : undefined;

  // Image
  const [selectedImageId, setSelectedImageId] = useState('');

  // Configuration
  const [sandboxName, setSandboxName] = useState('');
  const [selectedShapePreset, setSelectedShapePreset] = useState<string>('Mini');

  // Policies & Ports
  const [maxIdleSeconds, setMaxIdleSeconds] = useState(300);
  const [maxLifetimeSeconds, setMaxLifetimeSeconds] = useState(28800);
  const [ports, setPorts] = useState<PortConfiguration[]>([]);
  const [tags, setTags] = useState<{ key: string; value: string }[]>([]);
  const poolImages = useMemo(() => {
    if (!pool) return [] as ImageCatalogEntry[];
    return getImagesByPool(pool.poolId, pool.imageIds);
  }, [pool, images, getImagesByPool]);

  const selectedImage = useMemo(
    () => poolImages.find((img) => img.imageId === selectedImageId),
    [poolImages, selectedImageId],
  );

  const currentShape: SandboxShape = useMemo(() => {
    const preset = SHAPE_PRESETS.find((p) => p.label === selectedShapePreset);
    return preset?.shape ?? SHAPE_PRESETS[2].shape;
  }, [selectedShapePreset]);

  const tagsRecord: Record<string, string> = useMemo(() => {
    const rec: Record<string, string> = {};
    for (const t of tags) {
      if (t.key.trim()) rec[t.key.trim()] = t.value;
    }
    return rec;
  }, [tags]);

  // --- Fetch images on mount ---
  useEffect(() => {
    if (selectedPoolId) {
      fetchImages(selectedPoolId);
    }
  }, [selectedPoolId, fetchImages]);

  // --- Validation per step ---
  const canProceed = useCallback((): boolean => {
    const currentStepName = STEPS[step];
    switch (currentStepName) {
      case 'Pool':
        return !!wizardPoolId;
      case 'Image':
        return !!selectedImageId;
      case 'Configuration':
        return sandboxName.trim().length > 0;
      case 'Policies & Ports':
        return true;
      case 'Review':
        return true;
      default:
        return false;
    }
  }, [step, STEPS, wizardPoolId, selectedImageId, sandboxName]);

  // --- Handlers ---
  const handleNext = () => {
    if (step < maxStep && canProceed()) {
      // When moving from Pool step, reset image selection (pool changed)
      if (STEPS[step] === 'Pool') {
        setSelectedImageId('');
        if (wizardPoolId) fetchImages(wizardPoolId);
      }
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleAddPort = () => {
    setPorts((prev) => [...prev, { port: 8080, policy: 'Owner', protocol: 'Http' }]);
  };

  const handleRemovePort = (index: number) => {
    setPorts((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePortChange = (index: number, field: keyof PortConfiguration, value: string | number) => {
    setPorts((prev) =>
      prev.map((p, i) => {
        if (i !== index) return p;
        const updated = { ...p, [field]: value };
        // When switching to Custom, initialize authorizedCallers if empty
        if (field === 'policy' && value === 'Custom' && (!p.authorizedCallers || p.authorizedCallers.length === 0)) {
          updated.authorizedCallers = [{ tenantId: '', objectId: '' }];
        }
        // When switching to Owner, remove authorizedCallers
        if (field === 'policy' && value === 'Owner') {
          delete updated.authorizedCallers;
        }
        return updated;
      }),
    );
  };

  const handleAddCaller = (portIndex: number) => {
    setPorts((prev) =>
      prev.map((p, i) =>
        i === portIndex
          ? { ...p, authorizedCallers: [...(p.authorizedCallers ?? []), { tenantId: '', objectId: '' }] }
          : p,
      ),
    );
  };

  const handleRemoveCaller = (portIndex: number, callerIndex: number) => {
    setPorts((prev) =>
      prev.map((p, i) =>
        i === portIndex
          ? { ...p, authorizedCallers: (p.authorizedCallers ?? []).filter((_, ci) => ci !== callerIndex) }
          : p,
      ),
    );
  };

  const handleCallerChange = (portIndex: number, callerIndex: number, field: 'tenantId' | 'objectId', value: string) => {
    setPorts((prev) =>
      prev.map((p, i) =>
        i === portIndex
          ? {
              ...p,
              authorizedCallers: (p.authorizedCallers ?? []).map((c, ci) =>
                ci === callerIndex ? { ...c, [field]: value } : c,
              ),
            }
          : p,
      ),
    );
  };

  const handleAddTag = () => {
    setTags((prev) => [...prev, { key: '', value: '' }]);
  };

  const handleRemoveTag = (index: number) => {
    setTags((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTagChange = (index: number, field: 'key' | 'value', value: string) => {
    setTags((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };

  const handleCreate = async () => {
    if (!selectedPoolId || !selectedImageId) return;

    const request: CheckoutRequest = {
      sandboxName: sandboxName.trim(),
      imageId: selectedImageId,
      shape: currentShape,
      policies: { maxIdleInSeconds: maxIdleSeconds, maxLifetimeInSeconds: maxLifetimeSeconds },
      ...(ports.length > 0 ? { ports } : {}),
      ...(Object.keys(tagsRecord).length > 0 ? { tags: tagsRecord } : {}),
    };

    setIsCreating(true);
    setCreateError(null);
    try {
      const sandbox = await checkoutSandbox(selectedPoolId, request);
      toast.success(`Sandbox "${sandboxName}" created`);
      onCreated?.(sandbox);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setCreateError(err);
      } else {
        setCreateError(new ApiError(0, 'An unexpected error occurred'));
      }
    } finally {
      setIsCreating(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Step Indicator
  // ---------------------------------------------------------------------------

  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-1 px-6 py-4 border-b border-surface-200 dark:border-white/[0.06]">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center">
          <div className="flex items-center gap-2">
            <div
              className={clsx(
                'w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors duration-150',
                i < step && 'bg-brand-500 text-white',
                i === step && 'bg-brand-500 text-white ring-2 ring-brand-500/30',
                i > step && 'bg-surface-200 dark:bg-white/[0.06] text-surface-500 dark:text-surface-400',
              )}
            >
              {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </div>
            <span
              className={clsx(
                'text-xs font-medium hidden sm:inline',
                i <= step
                  ? 'text-surface-900 dark:text-surface-100'
                  : 'text-surface-400 dark:text-surface-500',
              )}
            >
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <ChevronRight className="w-4 h-4 mx-2 text-surface-300 dark:text-surface-700 flex-shrink-0" />
          )}
        </div>
      ))}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Step 0 (multi-pool) — Pool Selection
  // ---------------------------------------------------------------------------

  const renderPoolStep = () => {
    const availablePools = selectedPoolIds.length > 0
      ? pools.filter(p => selectedPoolIds.includes(p.poolId))
      : pools;

    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
            Select a Pool
          </h3>
          <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
            Choose which pool to create the sandbox in
          </p>
        </div>
        <div className="space-y-2">
          {availablePools.map((p) => (
            <button
              key={p.poolId}
              type="button"
              onClick={() => setWizardPoolId(p.poolId)}
              className={clsx(
                'w-full flex items-center gap-3 p-3 rounded-fluent-lg border transition-all text-left',
                wizardPoolId === p.poolId
                  ? 'border-brand-500 bg-brand-500/5 dark:bg-brand-500/10'
                  : 'border-surface-200 dark:border-white/[0.06] hover:border-surface-300 dark:hover:border-white/[0.1]'
              )}
            >
              <div className={clsx(
                'w-10 h-10 rounded-fluent-md flex items-center justify-center flex-shrink-0',
                wizardPoolId === p.poolId
                  ? 'bg-brand-500 text-white'
                  : 'bg-surface-100 dark:bg-white/[0.06] text-surface-500'
              )}>
                <Server className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-surface-900 dark:text-surface-100">
                  {p.displayName}
                </p>
                <p className="text-xs text-surface-500 dark:text-surface-400">
                  {p.region} &middot; {p.imageIds.length} images
                </p>
              </div>
              {wizardPoolId === p.poolId && (
                <Check className="w-5 h-5 text-brand-500 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Image Selection
  // ---------------------------------------------------------------------------

  const renderImageStep = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
          Select an Image
        </h3>
        <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
          Choose the base image for your sandbox.
        </p>
      </div>

      {poolImages.length === 0 ? (
        <div className="text-center py-8 text-sm text-surface-500">
          No images available for the selected pool.
        </div>
      ) : (
        <div className="grid gap-3">
          {poolImages.map((img) => (
            <button
              key={img.imageId}
              type="button"
              onClick={() => setSelectedImageId(img.imageId)}
              className={clsx(
                'card card-hover w-full text-left p-4 flex items-start gap-4 cursor-pointer transition-all duration-150',
                selectedImageId === img.imageId &&
                  'ring-2 ring-brand-500 border-brand-500 dark:border-brand-400',
              )}
            >
              {/* OS icon */}
              <div
                className={clsx(
                  'flex-shrink-0 w-10 h-10 rounded-fluent flex items-center justify-center',
                  'bg-surface-100 dark:bg-white/[0.06]',
                )}
              >
                {img.os === 'windows' ? (
                  <Monitor className="w-5 h-5 text-brand-500" />
                ) : (
                  <Terminal className="w-5 h-5 text-green-600 dark:text-green-400" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-surface-900 dark:text-surface-100 truncate">
                    {img.name}
                  </span>
                  {selectedImageId === img.imageId && (
                    <Check className="w-4 h-4 text-brand-500 flex-shrink-0" />
                  )}
                </div>
                {img.description && (
                  <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5 line-clamp-2">
                    {img.description}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="badge bg-surface-100 dark:bg-white/[0.06] text-surface-600 dark:text-surface-300">
                    rev {img.latestRevision}
                  </span>
                  {img.tags.map((tag) => (
                    <span
                      key={tag}
                      className="badge bg-brand-500/10 dark:bg-brand-500/15 text-brand-600 dark:text-brand-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Step 2 — Configuration
  // ---------------------------------------------------------------------------

  const renderConfigStep = () => (
    <div className="space-y-5">
      {/* Sandbox name */}
      <div>
        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
          Sandbox Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          required
          className="input"
          value={sandboxName}
          onChange={(e) => setSandboxName(e.target.value)}
          placeholder="my-sandbox"
          autoFocus
        />
      </div>

      {/* Shape presets */}
      <div>
        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
          Shape
        </label>
        <div className="flex flex-wrap gap-2">
          {SHAPE_PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              disabled={preset.disabled}
              onClick={() => !preset.disabled && setSelectedShapePreset(preset.label)}
              title={preset.disabled ? 'Coming soon' : undefined}
              className={clsx(
                'btn text-xs',
                preset.disabled
                  ? 'opacity-40 cursor-not-allowed bg-transparent text-surface-500 dark:text-surface-500 border border-surface-200 dark:border-surface-700'
                  : selectedShapePreset === preset.label
                    ? 'bg-brand-500 text-white hover:bg-brand-600'
                    : 'bg-transparent text-surface-700 dark:text-surface-200 border border-surface-300 dark:border-surface-700 hover:bg-surface-100 dark:hover:bg-white/[0.06]',
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Shape summary */}
        <p className="text-xs text-surface-500 dark:text-surface-400 mt-2">
          {formatShape(currentShape.processorCount, currentShape.memorySize)}
        </p>
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Step 3 — Policies & Ports
  // ---------------------------------------------------------------------------

  const renderPoliciesStep = () => (
    <div className="space-y-5">
      {/* Max Idle Timeout */}
      <div>
        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
          Max Idle Timeout
        </label>
        <div className="relative">
          <select
            className="input pr-10 appearance-none cursor-pointer"
            value={maxIdleSeconds}
            onChange={(e) => setMaxIdleSeconds(Number(e.target.value))}
          >
            <option value={300}>5 min</option>
            <option value={900}>15 min</option>
            <option value={1800}>30 min</option>
            <option value={3600}>1 hour</option>
            <option value={7200}>2 hours</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400 pointer-events-none" />
        </div>
      </div>

      {/* Max Lifetime */}
      <div>
        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
          Max Lifetime (capped at 24h)
        </label>
        <div className="relative">
          <select
            className="input pr-10 appearance-none cursor-pointer"
            value={maxLifetimeSeconds}
            onChange={(e) => setMaxLifetimeSeconds(Number(e.target.value))}
          >
            <option value={3600}>1 hour</option>
            <option value={14400}>4 hours</option>
            <option value={28800}>8 hours</option>
            <option value={43200}>12 hours</option>
            <option value={86400}>24 hours</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400 pointer-events-none" />
        </div>
      </div>

      {/* Ports */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-surface-700 dark:text-surface-300">
            Ports <span className="text-xs font-normal text-surface-400">(optional)</span>
          </label>
          <button type="button" onClick={handleAddPort} className="btn-subtle text-xs px-2 py-1">
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Port
          </button>
        </div>

        {ports.length > 0 && (
          <div className="space-y-3">
            {ports.map((p, i) => (
              <div key={i} className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    className="input flex-1 min-w-0"
                    value={p.port}
                    min={1}
                    max={65535}
                    onChange={(e) => handlePortChange(i, 'port', parseInt(e.target.value, 10) || 0)}
                    placeholder="Port"
                  />
                  <div className="relative flex-1 min-w-0">
                    <select
                      className="input pr-8 appearance-none cursor-pointer"
                      value={p.policy}
                      onChange={(e) => handlePortChange(i, 'policy', e.target.value)}
                    >
                      {PORT_AUTH_POLICIES.map((pol) => (
                        <option key={pol} value={pol}>
                          {pol}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-surface-400 pointer-events-none" />
                  </div>
                  <div className="relative flex-1 min-w-0">
                    <select
                      className="input pr-8 appearance-none cursor-pointer"
                      value={p.protocol}
                      onChange={(e) => handlePortChange(i, 'protocol', e.target.value)}
                    >
                      {PORT_PROTOCOLS.map((proto) => (
                        <option key={proto} value={proto}>
                          {proto}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-surface-400 pointer-events-none" />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemovePort(i)}
                    className="btn-subtle p-1.5 text-danger"
                    title="Remove port"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-xs text-surface-400 dark:text-surface-500 ml-1">
                  Owner = sandbox creator only. Custom = specify authorized callers.
                </p>
                {p.policy === 'Custom' && (
                  <div className="ml-4 space-y-2">
                    {(p.authorizedCallers ?? []).map((caller, ci) => (
                      <div key={ci} className="flex items-center gap-2">
                        <input
                          type="text"
                          className="input flex-1"
                          value={caller.tenantId}
                          onChange={(e) => handleCallerChange(i, ci, 'tenantId', e.target.value)}
                          placeholder="Tenant ID"
                        />
                        <input
                          type="text"
                          className="input flex-1"
                          value={caller.objectId}
                          onChange={(e) => handleCallerChange(i, ci, 'objectId', e.target.value)}
                          placeholder="Object ID"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveCaller(i, ci)}
                          className="btn-subtle p-1.5 text-danger"
                          title="Remove caller"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => handleAddCaller(i)}
                      className="btn-subtle text-xs px-2 py-1"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      Add Caller
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tags */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-surface-700 dark:text-surface-300">
            Tags <span className="text-xs font-normal text-surface-400">(optional)</span>
          </label>
          <button type="button" onClick={handleAddTag} className="btn-subtle text-xs px-2 py-1">
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Tag
          </button>
        </div>

        {tags.length > 0 && (
          <div className="space-y-2">
            {tags.map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  className="input flex-1"
                  value={t.key}
                  onChange={(e) => handleTagChange(i, 'key', e.target.value)}
                  placeholder="Key"
                />
                <span className="text-surface-400">=</span>
                <input
                  type="text"
                  className="input flex-1"
                  value={t.value}
                  onChange={(e) => handleTagChange(i, 'value', e.target.value)}
                  placeholder="Value"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveTag(i)}
                  className="btn-subtle p-1.5 text-danger"
                  title="Remove tag"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Step 4 — Review
  // ---------------------------------------------------------------------------

  const renderReviewStep = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
          Review & Create
        </h3>
        <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
          Confirm your sandbox configuration before creating.
        </p>
      </div>

      <table className="w-full text-sm">
        <tbody>
          {pool && (
            <tr className="border-b border-surface-100 dark:border-white/[0.06]/60">
              <td className="py-2 pr-4 text-surface-500 dark:text-surface-400 font-medium w-36">
                Pool
              </td>
              <td className="py-2 text-surface-900 dark:text-surface-100">
                {pool.displayName} <span className="text-xs text-surface-400">({pool.poolId})</span>
              </td>
            </tr>
          )}
          <tr className="border-b border-surface-100 dark:border-white/[0.06]/60">
            <td className="py-2 pr-4 text-surface-500 dark:text-surface-400 font-medium w-36">
              Name
            </td>
            <td className="py-2 text-surface-900 dark:text-surface-100">{sandboxName}</td>
          </tr>
          <tr className="border-b border-surface-100 dark:border-white/[0.06]/60">
            <td className="py-2 pr-4 text-surface-500 dark:text-surface-400 font-medium">Image</td>
            <td className="py-2 text-surface-900 dark:text-surface-100">
              {selectedImage?.name ?? selectedImageId}
            </td>
          </tr>
          <tr className="border-b border-surface-100 dark:border-white/[0.06]/60">
            <td className="py-2 pr-4 text-surface-500 dark:text-surface-400 font-medium">Shape</td>
            <td className="py-2 text-surface-900 dark:text-surface-100">
              {formatShape(currentShape.processorCount, currentShape.memorySize)}
              {selectedShapePreset !== 'Custom' && (
                <span className="ml-2 text-xs text-surface-400">({selectedShapePreset})</span>
              )}
            </td>
          </tr>
          <tr className="border-b border-surface-100 dark:border-white/[0.06]/60">
            <td className="py-2 pr-4 text-surface-500 dark:text-surface-400 font-medium">
              Max Idle
            </td>
            <td className="py-2 text-surface-900 dark:text-surface-100">
              {formatDuration(maxIdleSeconds)}
            </td>
          </tr>
          <tr className="border-b border-surface-100 dark:border-white/[0.06]/60">
            <td className="py-2 pr-4 text-surface-500 dark:text-surface-400 font-medium">
              Max Lifetime
            </td>
            <td className="py-2 text-surface-900 dark:text-surface-100">
              {formatDuration(maxLifetimeSeconds)}
            </td>
          </tr>
          {ports.length > 0 && (
            <tr className="border-b border-surface-100 dark:border-white/[0.06]/60">
              <td className="py-2 pr-4 text-surface-500 dark:text-surface-400 font-medium align-top">
                Ports
              </td>
              <td className="py-2 text-surface-900 dark:text-surface-100">
                <div className="space-y-2">
                  {ports.map((p, i) => (
                    <div key={i}>
                      <div className="text-xs font-mono">
                        {p.port} / {p.protocol} ({p.policy})
                      </div>
                      {p.policy === 'Custom' && p.authorizedCallers && p.authorizedCallers.length > 0 && (
                        <div className="ml-4 mt-1 space-y-0.5">
                          {p.authorizedCallers.map((c, ci) => (
                            <div key={ci} className="text-xs text-surface-500 dark:text-surface-400 font-mono">
                              tenant: {c.tenantId || '(empty)'} / object: {c.objectId || '(empty)'}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </td>
            </tr>
          )}
          {Object.keys(tagsRecord).length > 0 && (
            <tr>
              <td className="py-2 pr-4 text-surface-500 dark:text-surface-400 font-medium align-top">
                Tags
              </td>
              <td className="py-2 text-surface-900 dark:text-surface-100">
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(tagsRecord).map(([k, v]) => (
                    <span
                      key={k}
                      className="badge bg-surface-100 dark:bg-white/[0.06] text-surface-600 dark:text-surface-300"
                    >
                      {k}={v}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal max-w-2xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-surface-200 dark:border-white/[0.06] flex-shrink-0">
          <h2 className="text-base font-semibold text-surface-900 dark:text-surface-100">
            Create Sandbox
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="btn-subtle p-1.5"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step Indicator */}
        <StepIndicator />

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-5">
          {STEPS[step] === 'Pool' && renderPoolStep()}
          {STEPS[step] === 'Image' && renderImageStep()}
          {STEPS[step] === 'Configuration' && renderConfigStep()}
          {STEPS[step] === 'Policies & Ports' && renderPoliciesStep()}
          {STEPS[step] === 'Review' && renderReviewStep()}
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-surface-200 dark:border-white/[0.06] flex-shrink-0">
          <div>
            {step > 0 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={isCreating}
                className="btn-secondary"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isCreating}
              className="btn-subtle"
            >
              Cancel
            </button>
            {step < maxStep ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canProceed()}
                className="btn-primary"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCreate}
                disabled={isCreating || !canProceed()}
                className="btn-primary"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Sandbox'
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error modal overlay */}
      {createError && (
        <ApiErrorModal
          error={createError}
          onDismiss={() => setCreateError(null)}
          onRetry={() => {
            setCreateError(null);
            handleCreate();
          }}
        />
      )}
    </div>
  );
}
